import { useEffect, useRef, useState } from 'react';

/**
 * Reveal `length` characters progressively, the way the Neural Assistant types
 * its replies (~130 chars/s), but never taking longer than `maxSeconds`: a
 * 2,500-character mission turn at a fixed speed would take ~20 s.
 * Off when `animate` is false or the reader prefers reduced motion.
 */
export function useTypewriter(length: number, animate: boolean, opts: { maxSeconds?: number; onTick?: () => void; onDone?: () => void } = {}) {
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const active = animate && !reduced;
  const [shown, setShown] = useState(active ? 0 : length);
  const cbs = useRef(opts);
  cbs.current = opts;

  useEffect(() => {
    if (!active) { setShown(length); return; }
    const cps = Math.max(130, length / (cbs.current.maxSeconds ?? 6));
    let raf = 0;
    let start: number | undefined;
    const step = (t: number) => {
      start ??= t;
      const n = Math.min(length, Math.floor(((t - start) / 1000) * cps));
      setShown(n);
      cbs.current.onTick?.();
      if (n < length) raf = requestAnimationFrame(step);
      else cbs.current.onDone?.();
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [length, active]);

  const skip = () => { setShown(length); cbs.current.onDone?.(); };
  return { shown: Math.min(shown, length), typing: shown < length, skip };
}
